import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { SortOrder, SortProperty } from "../models/enums";
import { Filter } from "../models/event/filter";
import { Event } from "../models/event/event";
import { EventListItem } from "../models/event/event-list-item";
import { EventQuery } from "../models/event/event-query";
import { EventsResponse } from "../models/event/events-response";
import { Paging } from "../models/event/paging";
import { Sorting } from "../models/event/sorting";
import { AutoUnsubscribe } from "../shared/decorators";
import { FilterService } from "./filter.service";
import { EventRequest } from "../models/event/event-request";
import { SignalRService } from "./signal-r.service";
import { StatusItem } from "../models/status/status-response";
import { buildUrl } from "../shared/url.utils";

/** Service to handle events API plus event-list sort/paging state */
@Injectable({
  providedIn: "root",
})
@AutoUnsubscribe()
export class EventService implements OnDestroy {
  private readonly apiSegment = "Event";
  private readonly querySegment = "Query";
  private readonly eventHub = "event";

  private triggerForGetEvents = new Subject<boolean>();

  private _selectedEvent = new BehaviorSubject<Event | null>(null);
  public selectedEvent = this._selectedEvent.asObservable();

  //#region Event list sort / paging
  private currentSort = SortProperty.StartTime;
  public currentSortChanged = new Subject<SortProperty>();

  private readonly defaultPageNumber = 0;
  private readonly defaultPageLimit = 10;
  private paging = new Paging(this.defaultPageLimit, this.defaultPageNumber);
  private pageChange = new Subject<number>();
  //#endregion

  private readonly headers = { "Api-Version": environment.actionApi.version };

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    private signalRService: SignalRService
  ) {
    this.subscribeToChanges();
  }

  public selectEvent(event: Event | null): void {
    this._selectedEvent.next(event);
  }

  public getSelectedEvent(): Event | null {
    return this._selectedEvent.getValue();
  }

  public getCurrentSort(): SortProperty {
    return this.currentSort;
  }

  public setCurrentSort(sort: SortProperty) {
    this.currentSort = sort;
    this.currentSortChanged.next(sort);
  }

  public getPaging(): Paging {
    return this.paging;
  }

  public setPageNumber(page: number) {
    this.paging.page = page;
  }

  public resetPaging() {
    this.paging = new Paging(this.defaultPageLimit, this.defaultPageNumber);
  }

  public emitPageChange(value: number): void {
    this.pageChange.next(value);
  }

  //#region Public

  public connectToEventsSignalRService(connectFunction: Function[]): void {
    this.signalRService.createConnection(
      buildUrl([
        environment.actionApi.path.replace("/api", ""),
        this.eventHub,
      ]),
      connectFunction,
      environment.config.actionScope
    );
  }

  public setStatusOnTrafficAction(statusItem: StatusItem): void {
    const eventActions = this._selectedEvent.getValue()?.actions;
    if (eventActions) {
      let action = eventActions.find(
        (x) => x.trafficActionId === statusItem.id
      );
      if (action) {
        action.status = statusItem.s;
      }
    }
  }

  /** Get array of events from API based on filter, sort and paging */
  getEvents(): Observable<EventsResponse> {
    const request = this.getEventQuery(
      this.currentSort,
      this.filterService.getFilter()
    );
    return this.httpClient
      .post<EventsResponse>(
        buildUrl([
          environment.actionApi.path,
          this.apiSegment,
          this.querySegment,
        ]),
        request,
        { headers: this.headers }
      )
      .pipe(
        map((eventView) => {
          const eventResponseItem: EventsResponse = {
            totalPages: eventView.totalPages,
            totalItems: eventView.totalItems,
            currentItems: eventView.currentItems,
            currentPage: eventView.currentPage,
            events: eventView.events.map((eItem) => {
              eItem = new EventListItem(eItem);
              return eItem;
            }),
          };
          return eventResponseItem;
        })
      );
  }

  /** Create a new event */
  createEvent(event: Event): Observable<Event> {
    const eventRequest = this.convertToEventRequest(event);
    return this.httpClient.post<Event>(
      buildUrl([environment.actionApi.path, this.apiSegment]),
      eventRequest,
      { headers: this.headers }
    );
  }

  /** Update an already existing event */
  updateEvent(event: Event): Observable<Event> {
    const eventRequest = this.convertToEventRequest(event);
    return this.httpClient.put<Event>(
      buildUrl([
        environment.actionApi.path,
        this.apiSegment,
        event.id!.toString(),
      ]),
      eventRequest,
      { headers: this.headers }
    );
  }

  deleteEventIfEmpty(eventId: number): Observable<object> {
    return this.httpClient.delete(
      buildUrl([
        environment.actionApi.path,
        this.apiSegment,
        eventId.toString(),
      ]),
      { headers: this.headers }
    );
  }

  /** Get a single event based on ID */
  getEvent(eventId: number): Observable<Event> {
    return this.httpClient.get<Event>(
      buildUrl([
        environment.actionApi.path,
        this.apiSegment,
        eventId.toString(),
      ]),
      { headers: this.headers }
    );
  }

  getTriggerForGetEvents(): Observable<boolean> {
    return this.triggerForGetEvents;
  }
  emitTriggerForGetEvents(value: boolean): void {
    this.triggerForGetEvents.next(value);
  }
  //#endregion

  //#region Private
  private subscribeToChanges(): void {
    this.pageChange.subscribe((change) => {
      // Page 1 is number 0 when getting events
      this.paging.page = change - 1;
      this.triggerForGetEvents.next(true);
    });

    this.filterService.getUpdateEventsOnFilteringChange().subscribe(() => {
      this.triggerGetEventsOnFilter();
    });
  }

  private getEventQuery(sorting: SortProperty, filter: Filter) {
    let sortOrder = SortOrder.Descending;
    if (sorting == SortProperty.Alphabetically) {
      sortOrder = SortOrder.Ascending;
    }
    return new EventQuery(
      filter,
      this.paging,
      new Sorting(sorting, sortOrder)
    );
  }

  private convertToEventRequest(event: Event): EventRequest {
    return {
      reasonId: event.reasonId,
      trains: event.filterGeneratedTrains,
      comment: event.comment ?? "",
      filter: event.filter,
    };
  }

  private triggerGetEventsOnFilter(): void {
    this.resetPaging();
    this.triggerForGetEvents.next(true);
  }
  //#endregion

  ngOnDestroy() {}
}
